import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import { CONTACT_EMAIL } from "../content";
import { track } from "../lib/analytics";
import StarField from "../components/StarField";

const LAST_UPDATED = "2026-06-28";

const legalCopy = {
  privacy: {
    ES: {
      eyebrow: "// LEGAL · PRIVACIDAD",
      title: "Política de privacidad",
      subtitle:
        "Esta página explica qué datos puede tratar Scopelink.io, para qué los usa y cómo puedes ejercer tus derechos.",
      back: "Volver al inicio",
      updated: "Última actualización",
      contact: "Contacto de privacidad",
      sections: [
        {
          title: "Responsable y alcance",
          body: [
            "Scopelink.io es responsable del tratamiento de los datos personales recogidos a través de este sitio web y de los canales de contacto vinculados desde la página.",
            "Esta política cubre la web pública de Scopelink.io. Las funciones de la plataforma, si contratas o pruebas el servicio, podrán requerir avisos o acuerdos adicionales según la configuración de tu observatorio.",
          ],
        },
        {
          title: "Datos que podemos recoger",
          body: [
            "Datos que nos envías voluntariamente, como nombre, correo electrónico, organización, mensaje y cualquier información incluida en formularios o correos de contacto.",
            "Datos técnicos básicos de navegación, como dirección IP aproximada, páginas visitadas, dispositivo, navegador, idioma, fecha y hora. Estos datos se usan principalmente para seguridad, diagnóstico y medición estadística de visitas.",
          ],
        },
        {
          title: "Finalidades del tratamiento",
          body: [
            "Responder a solicitudes de información, soporte, presupuestos o contacto comercial.",
            "Mantener la seguridad, disponibilidad y funcionamiento del sitio web.",
            "Medir de forma agregada el uso de la web mediante Google Analytics u otra herramienta estadística similar, siempre limitada a analítica de acceso.",
            "Enviar información comercial únicamente si existe una base legal válida o tu consentimiento. En el futuro podríamos mostrar publicidad contextual o integrar servicios como Google AdSense, actualizando esta política cuando corresponda.",
          ],
        },
        {
          title: "Compartición de datos",
          body: [
            "No vendemos tus datos ni damos acceso a terceros para usos propios ajenos a Scopelink.io.",
            "Podemos usar proveedores técnicos necesarios para operar la web, como alojamiento, correo, analítica o seguridad. Estos proveedores solo deben tratar datos siguiendo nuestras instrucciones o sus obligaciones legales.",
            "También podremos comunicar datos si una ley, autoridad competente o proceso legal válido lo exige.",
          ],
        },
        {
          title: "Conservación",
          body: [
            "Conservamos los datos de contacto durante el tiempo necesario para gestionar tu solicitud y mantener una relación comercial o técnica razonable.",
            "Los datos estadísticos se conservan en periodos agregados o limitados según la configuración de la herramienta analítica utilizada.",
          ],
        },
        {
          title: "Tus derechos",
          body: [
            "Puedes solicitar acceso, rectificación, supresión, oposición, limitación del tratamiento y portabilidad cuando sea aplicable.",
            `Para ejercerlos, escribe a ${CONTACT_EMAIL}. Puede que necesitemos verificar tu identidad antes de responder.`,
          ],
        },
      ],
    },
    EN: {
      eyebrow: "// LEGAL · PRIVACY",
      title: "Privacy policy",
      subtitle:
        "This page explains what data Scopelink.io may process, why it is used, and how you can exercise your rights.",
      back: "Back home",
      updated: "Last updated",
      contact: "Privacy contact",
      sections: [
        { title: "Controller and scope", body: ["Scopelink.io is responsible for personal data collected through this website and the contact channels linked from it.", "This policy covers the public Scopelink.io website. Platform features, if you subscribe to or test the service, may require additional notices or agreements depending on your observatory configuration."] },
        { title: "Data we may collect", body: ["Data you voluntarily send us, such as name, email address, organization, message and any information included in forms or contact emails.", "Basic technical browsing data, such as approximate IP address, pages visited, device, browser, language, date and time. This is mainly used for security, diagnostics and statistical access measurement."] },
        { title: "Purposes", body: ["Responding to information, support, quotation or commercial contact requests.", "Maintaining website security, availability and operation.", "Measuring aggregated website usage through Google Analytics or a similar statistics tool, limited to access analytics.", "Sending commercial information only when there is a valid legal basis or your consent. In the future we may show contextual advertising or integrate services such as Google AdSense, and will update this policy when appropriate."] },
        { title: "Data sharing", body: ["We do not sell your data or give third parties access for their own purposes unrelated to Scopelink.io.", "We may use technical providers needed to operate the website, such as hosting, email, analytics or security. These providers should process data only under our instructions or their legal duties.", "We may also disclose data if required by law, a competent authority or a valid legal process."] },
        { title: "Retention", body: ["We keep contact data for as long as needed to handle your request and maintain a reasonable commercial or technical relationship.", "Statistical data is retained in aggregated or limited periods according to the analytics tool configuration."] },
        { title: "Your rights", body: ["You may request access, rectification, erasure, objection, restriction of processing and portability where applicable.", `To exercise these rights, write to ${CONTACT_EMAIL}. We may need to verify your identity before responding.`] },
      ],
    },
  },
  cookies: {
    ES: {
      eyebrow: "// LEGAL · COOKIES",
      title: "Política de cookies",
      subtitle: "Usamos cookies de forma limitada para recordar tu preferencia y medir estadísticas de acceso con Google Analytics.",
      back: "Volver al inicio",
      updated: "Última actualización",
      contact: "Contacto",
      sections: [
        { title: "Qué son las cookies", body: ["Las cookies son pequeños archivos o tecnologías similares que un sitio web puede guardar en tu navegador para recordar información o medir el uso del servicio."] },
        { title: "Cookies que usamos", body: ["Cookies o almacenamiento estrictamente necesario para recordar si aceptaste, rechazaste o cerraste el aviso de cookies.", "Cookies analíticas de Google Analytics para entender visitas, páginas consultadas, rendimiento y patrones agregados de uso. No las usamos para vender datos ni crear perfiles comerciales propios."] },
        { title: "Publicidad y servicios futuros", body: ["Actualmente no usamos cookies publicitarias para finalidades adicionales a la analítica estadística indicada.", "Si en el futuro integramos publicidad, por ejemplo Google AdSense, actualizaremos esta política y, cuando sea necesario, solicitaremos o ajustaremos el consentimiento antes de activar esas cookies."] },
        { title: "Cómo gestionar las cookies", body: ["Puedes aceptar o rechazar las cookies desde el banner inicial.", "También puedes borrar o bloquear cookies desde la configuración de tu navegador. Si bloqueas cookies necesarias, algunas preferencias de la web podrían no recordarse."] },
        { title: "Terceros", body: ["Google Analytics puede tratar datos técnicos de uso como proveedor de analítica. Su tratamiento depende de la configuración activa del servicio y de las opciones de privacidad disponibles en Google."] },
      ],
    },
    EN: {
      eyebrow: "// LEGAL · COOKIES",
      title: "Cookie policy",
      subtitle: "We use cookies in a limited way to remember your preference and measure access statistics with Google Analytics.",
      back: "Back home",
      updated: "Last updated",
      contact: "Contact",
      sections: [
        { title: "What cookies are", body: ["Cookies are small files or similar technologies that a website may store in your browser to remember information or measure service usage."] },
        { title: "Cookies we use", body: ["Strictly necessary cookies or storage to remember whether you accepted, rejected or closed the cookie notice.", "Google Analytics cookies to understand visits, viewed pages, performance and aggregated usage patterns. We do not use them to sell data or build our own commercial profiles."] },
        { title: "Advertising and future services", body: ["We currently do not use advertising cookies for purposes beyond the statistical analytics described above.", "If we integrate advertising in the future, for example Google AdSense, we will update this policy and, where required, request or adjust consent before enabling those cookies."] },
        { title: "How to manage cookies", body: ["You can accept or reject cookies from the initial banner.", "You can also delete or block cookies from your browser settings. If you block necessary cookies, some website preferences may not be remembered."] },
        { title: "Third parties", body: ["Google Analytics may process technical usage data as an analytics provider. Its processing depends on the active service configuration and privacy options available from Google."] },
      ],
    },
  },
};

export default function LegalPage({ type, lang, NavbarComponent }) {
  const handleContactClick = () => track("contact_click");
  const copy = legalCopy[type][lang] || legalCopy[type].EN;

  return (
    <div data-testid={`${type}-policy-root`} className="min-h-screen bg-[#030508] text-white">
      {NavbarComponent}
      <main className="relative overflow-hidden pt-28 md:pt-32 pb-24">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,184,0,0.12),transparent_30%),radial-gradient(circle_at_20%_20%,rgba(0,229,255,0.08),transparent_24%)]" aria-hidden="true" />
        <StarField count={34} />
        <article className="relative max-w-4xl mx-auto px-6 md:px-10">
          <Link to="/" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--primary)] transition-colors mb-8">
            <ArrowLeft className="h-3.5 w-3.5" /> {copy.back}
          </Link>
          <div className="border border-white/10 bg-[#0b101e]/70 backdrop-blur-xl p-6 md:p-10 mb-8">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--primary)] mb-5">{copy.eyebrow}</div>
            <h1 className="font-display text-4xl md:text-6xl font-black uppercase leading-tight mb-5">{copy.title}</h1>
            <p className="text-white/75 text-lg leading-relaxed max-w-3xl">{copy.subtitle}</p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2 font-mono text-[11px] tracking-[0.18em] uppercase text-white/50">
              <span>{copy.updated}: <time dateTime={LAST_UPDATED}>{LAST_UPDATED}</time></span>
              <a href={`mailto:${CONTACT_EMAIL}`} onClick={handleContactClick} className="inline-flex items-center gap-2 hover:text-[var(--primary)]"><Mail className="h-3.5 w-3.5" />{copy.contact}: {CONTACT_EMAIL}</a>
            </div>
          </div>

          <div className="space-y-6">
            {copy.sections.map((section) => (
              <section key={section.title} className="border border-white/10 bg-white/[0.03] p-6 md:p-8">
                <div className="flex items-start gap-3 mb-4">
                  <ShieldCheck className="h-5 w-5 text-[var(--primary)] mt-1 flex-shrink-0" />
                  <h2 className="font-display text-2xl font-bold text-white">{section.title}</h2>
                </div>
                <div className="space-y-4 text-white/70 text-base leading-[1.8]">
                  {section.body.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                </div>
              </section>
            ))}
          </div>
        </article>
      </main>
    </div>
  );
}
